import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function DockerPage(): Node {
  return html`
    <div>
      <h1>Deploy with Docker / Node Server</h1>
      <p>Containerize your onefold app with Nginx or a Node.js server for self-hosted environments. Use <strong>path-based routing</strong> with server-side SPA fallback.</p>

      <h2>Option A: Nginx in Docker (Recommended)</h2>

      <h3>Nginx Configuration</h3>
      <p>Create <code>nginx.conf</code>:</p>
      ${CodeBlock(`server {
  listen 80;
  server_name _;
  root /usr/share/nginx/html;
  index index.html;

  # SPA fallback — serve index.html for all routes
  location / {
    try_files $uri $uri/ /index.html;
  }

  # Cache static assets aggressively
  location ~* \\.(js|css|svg|png|jpg|woff2)$ {
    expires 1y;
    add_header Cache-Control "public, immutable";
  }

  # No cache for index.html
  location = /index.html {
    expires -1;
    add_header Cache-Control "no-store, must-revalidate";
  }
}`, 'nginx')}

      <h3>Dockerfile</h3>
      ${CodeBlock(`# Build stage
FROM node:22-alpine AS build
WORKDIR /app
COPY package*.json ./
RUN npm ci
COPY . .
RUN npm run build

# Production stage
FROM nginx:alpine
COPY --from=build /app/dist /usr/share/nginx/html
COPY nginx.conf /etc/nginx/conf.d/default.conf
EXPOSE 80
CMD ["nginx", "-g", "daemon off;"]`, 'dockerfile')}

      <h3>Build and Run</h3>
      ${CodeBlock(`docker build -t my-onefold-app .
docker run -p 8080:80 my-onefold-app`, 'bash')}

      ${Callout('The try_files directive is the key to SPA fallback — it tells Nginx to serve index.html when no matching file exists.')}

      <h2>Option B: Node.js Server</h2>
      <p>Use a minimal Express server with <code>express.static</code> and a fallback to <code>index.html</code>:</p>

      <h3>server.mjs</h3>
      ${CodeBlock(`import express from 'express';
import { dirname, resolve } from 'node:path';
import { fileURLToPath } from 'node:url';

const __dirname = dirname(fileURLToPath(import.meta.url));
const app = express();
const PORT = process.env.PORT || 3000;

// Serve static files from dist
app.use(express.static(resolve(__dirname, 'dist')));

// SPA fallback — all other routes serve index.html
app.get('*', (_req, res) => {
  res.sendFile(resolve(__dirname, 'dist/index.html'));
});

app.listen(PORT, () => {
  console.log(\`Server running on port \${PORT}\`);
});`)}

      <h3>Dockerfile (Node version)</h3>
      ${CodeBlock(`FROM node:22-alpine AS build
WORKDIR /app
COPY package*.json ./
RUN npm ci
COPY . .
RUN npm run build

FROM node:22-alpine
WORKDIR /app
COPY --from=build /app/dist ./dist
COPY --from=build /app/server.mjs .
COPY --from=build /app/package*.json ./
RUN npm ci --omit=dev
EXPOSE 3000
CMD ["node", "server.mjs"]`, 'dockerfile')}

      <h2>Router Configuration</h2>
      <p>With either Nginx or Node serving as SPA fallback, use the default path-based routing:</p>
      ${CodeBlock(`import { Router, mount } from 'onefold';

const app = Router([
  { path: '/', view: () => Home() },
  { path: '/dashboard', view: () => Dashboard() },
], () => NotFound());

mount(app, document.getElementById('app')!);`)}

      <h2>Docker Compose</h2>
      ${CodeBlock(`# docker-compose.yml
services:
  web:
    build: .
    ports:
      - "8080:80"
    restart: unless-stopped`, 'yaml')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/deployment/vercel">Vercel</a> — zero-config deployment</li>
        <li><a href="/deployment/github-pages">GitHub Pages</a> — free static hosting with hash routing</li>
        <li><a href="/ssr">Server-Side Rendering</a> — pre-render pages for SEO</li>
      </ul>
    </div>
  `;
}
